import React, { useCallback, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    Box, Button, DataTable, Form, FormField, Heading, Layer, Page, PageContent, PageHeader, Spinner, Text, TextArea, TextInput, ThemeContext,
} from "grommet";
import { Add, Edit, Trash } from "grommet-icons";
import { announcementService } from "../services";
import { NotificationContext } from "../components/admin/NotificationContext";
import { isFullAdmin } from "../components/admin/roles";

const FORM_FIELDS = ["title", "description", "end_date"];
const EMPTY_FORM = { title: "", description: "", end_date: "" };

const isExpired = (a) => Boolean(a.end_date) && new Date(a.end_date) < new Date();
const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
// datetime-local inputs take local time without a zone
const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const fieldError = (errors, name) => (errors && errors[name] ? [].concat(errors[name]).join(" ") : undefined);

const StatusBadge = ({ expired }) => (
    <Box
        alignSelf="start"
        pad={{ horizontal: "small", vertical: "xxsmall" }}
        round="small"
        background={{ color: expired ? "status-critical" : "status-ok", opacity: "weak" }}
    >
        <Text size="small" weight="bold">{expired ? "Expired" : "Active"}</Text>
    </Box>
);

export const AnnouncementForm = ({ initial, onCancel, onSubmit }) => {
    const [value, setValue] = useState(
        initial ? { title: initial.title, description: initial.description, end_date: toLocalInput(initial.end_date) } : EMPTY_FORM
    );
    const [errors, setErrors] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleSubmit = () => {
        setSaving(true);
        setErrors(null);
        onSubmit({
            title: value.title.trim(),
            description: value.description.trim(),
            end_date: value.end_date ? new Date(value.end_date).toISOString() : null,
        }).catch((error) => {
            setErrors(error.fieldErrors || { non_field_errors: [error.message] });
            setSaving(false);
        });
    };

    const otherErrors = errors
        ? Object.entries(errors).filter(([key]) => !FORM_FIELDS.includes(key)).flatMap(([, v]) => [].concat(v))
        : [];

    return (
        <Layer position="center" onEsc={onCancel} onClickOutside={onCancel}>
            <Box pad="medium" gap="medium" width="large">
                <Heading level={2} margin="none">{initial ? "Edit announcement" : "New announcement"}</Heading>
                <Form value={value} onChange={setValue} onSubmit={handleSubmit} validate="submit">
                    <FormField name="title" htmlFor="announcement-title" label="Title" required error={fieldError(errors, "title")}>
                        <TextInput id="announcement-title" name="title" maxLength={1000} />
                    </FormField>
                    <FormField name="description" htmlFor="announcement-description" label="Description" required error={fieldError(errors, "description")}>
                        <TextArea id="announcement-description" name="description" rows={5} resize="vertical" />
                    </FormField>
                    <FormField
                        name="end_date"
                        htmlFor="announcement-end-date"
                        label="End date (optional)"
                        help="Leave empty for an announcement that never expires"
                        error={fieldError(errors, "end_date")}
                    >
                        <TextInput id="announcement-end-date" name="end_date" type="datetime-local" />
                    </FormField>
                    {otherErrors.length > 0 && <Text color="status-critical">{otherErrors.join(" ")}</Text>}
                    <Box direction="row" justify="end" gap="small" margin={{ top: "medium" }}>
                        <Button label="Cancel" onClick={onCancel} disabled={saving} />
                        <Button type="submit" primary label={saving ? "Saving..." : "Save"} disabled={saving} />
                    </Box>
                </Form>
            </Box>
        </Layer>
    );
};

export const AdminAnnouncementsPage = () => {
    const access = useSelector((state) => state.session.user && state.session.user.access);
    const fullAdmin = useSelector((state) => isFullAdmin(state.session.user));
    const { showNotification } = useContext(NotificationContext);
    // primary button is white in dark mode and dark in light mode, so the icon inverts
    const primaryIconColor = useContext(ThemeContext).dark ? "#000000" : "#FFFFFF";
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    // "new" | announcement being edited | null
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [deleteInProgress, setDeleteInProgress] = useState(false);

    const load = useCallback(() => {
        if (!access || !fullAdmin) return;
        setLoading(true);
        announcementService.getAllAnnouncements(access)
            .then((data) => {
                setAnnouncements(Array.isArray(data) ? data : []);
                setLoadError(null);
            })
            .catch((error) => setLoadError(error.message))
            .finally(() => setLoading(false));
    }, [access, fullAdmin]);

    useEffect(() => { load(); }, [load]);

    const handleSave = (data) => {
        const isNew = editing === "new";
        const request = isNew
            ? announcementService.createAnnouncement(access, data)
            : announcementService.updateAnnouncement(access, editing.id, data);
        return request
            .then(() => {
                showNotification(isNew ? "Announcement created" : "Announcement updated", "normal");
                setEditing(null);
                load();
            })
            .catch((error) => {
                if (error.status !== 404) throw error;
                showNotification("Not found. The list has been refreshed.", "warning");
                setEditing(null);
                load();
            });
    };

    const handleDelete = () => {
        setDeleteInProgress(true);
        announcementService.deleteAnnouncement(access, deleting.id)
            .then(() => showNotification("Announcement deleted", "normal"))
            .catch((error) => showNotification(error.status === 404 ? "Not found. The list has been refreshed." : error.message, "critical"))
            .finally(() => {
                setDeleteInProgress(false);
                setDeleting(null);
                load();
            });
    };

    const columns = [
        { property: "title", header: "Title", size: "medium", render: (a) => <Text weight="bold" truncate title={a.title}>{a.title}</Text> },
        { property: "description", header: "Description", size: "medium", sortable: false, render: (a) => <Text truncate title={a.description}>{a.description}</Text> },
        { property: "status", header: "Status", size: "xsmall", sortable: false, render: (a) => <StatusBadge expired={isExpired(a)} /> },
        { property: "end_date", header: "Ends", size: "small", render: (a) => (a.end_date ? formatDate(a.end_date) : "Never") },
        { property: "created_at", header: "Created", size: "small", render: (a) => formatDate(a.created_at) },
        {
            property: "actions",
            header: "",
            size: "xsmall",
            sortable: false,
            render: (a) => (
                <Box direction="row" gap="xsmall">
                    <Button icon={<Edit />} a11yTitle={`Edit ${a.title}`} tip="Edit" onClick={() => setEditing(a)} />
                    <Button icon={<Trash />} a11yTitle={`Delete ${a.title}`} tip="Delete" onClick={() => setDeleting(a)} />
                </Box>
            ),
        },
    ];

    if (!fullAdmin) {
        return (
            <Page>
                <PageContent gap="medium">
                    <PageHeader title="User Announcements" />
                    <Text>You are not authorised to view this.</Text>
                </PageContent>
            </Page>
        );
    }

    return (
        <Page>
            <PageContent gap="medium">
                <PageHeader
                    title="User Announcements"
                    subtitle="Active announcements are shown to all users when they sign in."
                    actions={<Button primary icon={<Add color={primaryIconColor} />} label="New announcement" onClick={() => setEditing("new")} />}
                />
                {loading ? <Spinner /> : loadError ? (
                    <Text color="status-critical">{loadError}</Text>
                ) : announcements.length === 0 ? (
                    <Text color="text-weak">No announcements yet.</Text>
                ) : (
                    <Box overflow={{ horizontal: "auto" }}>
                        <DataTable columns={columns} data={announcements} primaryKey="id" sortable paginate step={20} />
                    </Box>
                )}

                {editing && (
                    <AnnouncementForm
                        initial={editing === "new" ? null : editing}
                        onCancel={() => setEditing(null)}
                        onSubmit={handleSave}
                    />
                )}

                {deleting && (
                    <Layer position="center" onEsc={() => setDeleting(null)} onClickOutside={() => setDeleting(null)}>
                        <Box pad="medium" gap="medium" width="medium">
                            <Heading level={3} margin="none">Delete announcement?</Heading>
                            <Text>"{deleting.title}" will be removed for all users. This can't be undone.</Text>
                            <Box direction="row" justify="end" gap="small">
                                <Button label="Cancel" onClick={() => setDeleting(null)} disabled={deleteInProgress} />
                                <Button primary label={deleteInProgress ? "Deleting..." : "Delete"} onClick={handleDelete} disabled={deleteInProgress} />
                            </Box>
                        </Box>
                    </Layer>
                )}
            </PageContent>
        </Page>
    );
};
